import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subWeeks, subMonths, addDays, parseISO } from "date-fns";
import { employeeApi, shiftApi, absenceApi, businessHoursApi, breakTypeApi, eventsApi } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { toast } from "sonner";
import { ArrowLeft, CalendarIcon, TrendingUp, Users, Clock, DollarSign, BarChart3, PieChart, LineChart, PartyPopper } from "lucide-react";
import { cn, getCurrencySymbol, calculateTotalHours, calculateBillableHours, calculateShiftCost, getSalaryDailyCost } from "../lib/utils";
import {
  BarChart,
  Bar,
  LineChart as RechartsLineChart,
  Line,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

// Chart colors
const CHART_COLORS = [
  "#8b5cf6", // violet
  "#06b6d4", // cyan
  "#10b981", // emerald
  "#f59e0b", // amber
  "#ef4444", // red
  "#ec4899", // pink
  "#6366f1", // indigo
  "#84cc16", // lime
];

const PIE_COLORS = ["#8b5cf6", "#06b6d4", "#10b981", "#f59e0b", "#ef4444", "#ec4899"];

// Custom tooltip component - defined outside Charts to avoid re-renders
const CustomTooltip = ({ active, payload, label, prefix = "", suffix = "" }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-popover border rounded-lg shadow-lg p-3">
        <p className="font-medium text-sm">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {prefix}{entry.value}{suffix}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const Charts = () => {
  const navigate = useNavigate();
  const { hasFeatureAccess } = useAuth();
  
  const [employees, setEmployees] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [events, setEvents] = useState([]);
  const [breakTypes, setBreakTypes] = useState([]);
  const [currency, setCurrency] = useState("GBP");
  const [loading, setLoading] = useState(true);
  
  // Date range state
  const [dateRange, setDateRange] = useState("this_week");
  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd] = useState(null);
  
  const hasWageCosts = hasFeatureAccess('wageCostCalculations');
  const hasEventAccess = hasFeatureAccess('eventManagement');

  // Calculate date range
  const { startDate, endDate, startStr, endStr } = useMemo(() => {
    const today = new Date();
    let start, end;
    
    switch (dateRange) {
      case "this_week":
        start = startOfWeek(today, { weekStartsOn: 1 });
        end = endOfWeek(today, { weekStartsOn: 1 });
        break;
      case "last_week":
        start = startOfWeek(subWeeks(today, 1), { weekStartsOn: 1 });
        end = endOfWeek(subWeeks(today, 1), { weekStartsOn: 1 });
        break;
      case "this_month":
        start = startOfMonth(today);
        end = endOfMonth(today);
        break;
      case "last_month":
        start = startOfMonth(subMonths(today, 1));
        end = endOfMonth(subMonths(today, 1));
        break;
      case "last_4_weeks":
        start = startOfWeek(subWeeks(today, 3), { weekStartsOn: 1 });
        end = endOfWeek(today, { weekStartsOn: 1 });
        break;
      case "custom":
        start = customStart || startOfWeek(today, { weekStartsOn: 1 });
        end = customEnd || endOfWeek(today, { weekStartsOn: 1 });
        break;
      default:
        start = startOfWeek(today, { weekStartsOn: 1 });
        end = endOfWeek(today, { weekStartsOn: 1 });
    }
    
    return {
      startDate: start,
      endDate: end,
      startStr: format(start, "yyyy-MM-dd"),
      endStr: format(end, "yyyy-MM-dd"),
    };
  }, [dateRange, customStart, customEnd]);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [empRes, shiftRes, absenceRes, breakRes, hoursRes, eventsRes] = await Promise.all([
          employeeApi.getAll(),
          shiftApi.getAll(startStr, endStr),
          absenceApi.getAll(null, startStr, endStr),
          breakTypeApi.getAll(),
          businessHoursApi.get(),
          eventsApi.getAll(startStr, endStr),
        ]);
        
        setEmployees(empRes.data);
        setShifts(shiftRes.data);
        setAbsences(absenceRes.data);
        setBreakTypes(breakRes.data);
        setCurrency(hoursRes.data?.currency || "GBP");
        setEvents(eventsRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to load chart data");
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [startStr, endStr]);

  const currencySymbol = getCurrencySymbol(currency);

  // Generate days in range
  const daysInRange = useMemo(() => {
    const days = [];
    let current = new Date(startDate);
    while (current <= endDate) {
      days.push(format(current, "yyyy-MM-dd"));
      current = addDays(current, 1);
    }
    return days;
  }, [startDate, endDate]);

  // ============ CHART DATA CALCULATIONS ============

  // 1. Hours per Employee (Bar Chart)
  const hoursPerEmployeeData = useMemo(() => {
    return employees.map(emp => {
      const empShifts = shifts.filter(s => s.employee_id === emp.id);
      const totalHours = empShifts.reduce((acc, shift) => {
        return acc + calculateBillableHours(shift, breakTypes);
      }, 0);
      return {
        name: emp.name.split(' ')[0], // First name only for space
        fullName: emp.name,
        hours: Math.round(totalHours * 100) / 100,
        color: emp.color,
      };
    }).filter(d => d.hours > 0).sort((a, b) => b.hours - a.hours);
  }, [employees, shifts, breakTypes]);

  // 2. Cost per Employee (Bar Chart)
  const costPerEmployeeData = useMemo(() => {
    return employees.map(emp => {
      const empShifts = shifts.filter(s => s.employee_id === emp.id);
      const totalCost = empShifts.reduce((acc, shift) => {
        return acc + calculateShiftCost(shift.start_time, shift.end_time, 0, shift, breakTypes, emp);
      }, 0);
      // Add salary costs for salary employees
      let salaryCost = 0;
      if (emp.pay_type === 'salary') {
        const workDays = daysInRange.filter(date => {
          return empShifts.some(s => s.date === date);
        }).length;
        salaryCost = workDays * getSalaryDailyCost(emp);
      }
      return {
        name: emp.name.split(' ')[0],
        fullName: emp.name,
        cost: Math.round((totalCost + salaryCost) * 100) / 100,
      };
    }).filter(d => d.cost > 0).sort((a, b) => b.cost - a.cost);
  }, [employees, shifts, breakTypes, daysInRange]);

  // 3. Shifts per Day of Week (Bar Chart)
  const shiftsPerDayOfWeek = useMemo(() => {
    const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const counts = [0, 0, 0, 0, 0, 0, 0];
    
    shifts.forEach(shift => {
      const date = parseISO(shift.date);
      const dayIndex = (date.getDay() + 6) % 7; // Monday = 0
      counts[dayIndex]++;
    });
    
    return dayNames.map((name, index) => ({
      name,
      shifts: counts[index],
    }));
  }, [shifts]);

  // 4. Daily Hours Trend (Line Chart)
  const dailyHoursTrend = useMemo(() => {
    return daysInRange.map(date => {
      const dayShifts = shifts.filter(s => s.date === date);
      const totalHours = dayShifts.reduce((acc, shift) => {
        return acc + calculateBillableHours(shift, breakTypes);
      }, 0);
      return {
        date: format(parseISO(date), "MMM d"),
        fullDate: date,
        hours: Math.round(totalHours * 100) / 100,
      };
    });
  }, [daysInRange, shifts, breakTypes]);

  // 5. Daily Cost Trend (Line Chart)
  const dailyCostTrend = useMemo(() => {
    return daysInRange.map(date => {
      const dayShifts = shifts.filter(s => s.date === date);
      let totalCost = 0;
      
      dayShifts.forEach(shift => {
        const emp = employees.find(e => e.id === shift.employee_id);
        if (emp) {
          totalCost += calculateShiftCost(shift.start_time, shift.end_time, 0, shift, breakTypes, emp);
          if (emp.pay_type === 'salary') {
            totalCost += getSalaryDailyCost(emp);
          }
        }
      });
      
      return {
        date: format(parseISO(date), "MMM d"),
        fullDate: date,
        cost: Math.round(totalCost * 100) / 100,
      };
    });
  }, [daysInRange, shifts, employees, breakTypes]);

  // 6. Cost Breakdown (Pie Chart)
  const costBreakdownData = useMemo(() => {
    let shiftCosts = 0;
    let salaryCosts = 0;
    let absenceCosts = 0;
    
    shifts.forEach(shift => {
      const emp = employees.find(e => e.id === shift.employee_id);
      if (emp) {
        if (emp.pay_type === 'hourly') {
          shiftCosts += calculateShiftCost(shift.start_time, shift.end_time, 0, shift, breakTypes, emp);
        } else {
          salaryCosts += getSalaryDailyCost(emp);
        }
      }
    });
    
    // Calculate absence costs (simplified)
    absences.forEach(absence => {
      const emp = employees.find(e => e.id === absence.employee_id);
      if (emp && absence.is_paid) {
        const days = Math.ceil((parseISO(absence.end_date) - parseISO(absence.start_date)) / (1000 * 60 * 60 * 24)) + 1;
        absenceCosts += days * (emp.hourly_rate || 0) * ((emp.contracted_hours || 40) / 5);
      }
    });
    
    return [
      { name: 'Hourly Staff', value: Math.round(shiftCosts * 100) / 100, color: '#8b5cf6' },
      { name: 'Salaried Staff', value: Math.round(salaryCosts * 100) / 100, color: '#06b6d4' },
      { name: 'Paid Absences', value: Math.round(absenceCosts * 100) / 100, color: '#f59e0b' },
    ].filter(d => d.value > 0);
  }, [shifts, employees, breakTypes, absences]);

  // 7. Staff Count per Day (Area Chart)
  const staffCountPerDay = useMemo(() => {
    return daysInRange.map(date => {
      const dayShifts = shifts.filter(s => s.date === date);
      const uniqueEmployees = new Set(dayShifts.map(s => s.employee_id));
      return {
        date: format(parseISO(date), "MMM d"),
        fullDate: date,
        staff: uniqueEmployees.size,
        shifts: dayShifts.length,
      };
    });
  }, [daysInRange, shifts]);

  // 8. Events per Day (Bar Chart)
  const eventsPerDay = useMemo(() => {
    return daysInRange.map(date => {
      const dayEvents = events.filter(e => e.date === date);
      const totalHours = dayEvents.reduce((acc, event) => {
        const [startH, startM] = event.start_time.split(':').map(Number);
        const [endH, endM] = event.end_time.split(':').map(Number);
        return acc + ((endH * 60 + endM) - (startH * 60 + startM)) / 60;
      }, 0);
      return {
        date: format(parseISO(date), "MMM d"),
        fullDate: date,
        events: dayEvents.length,
        hours: Math.round(totalHours * 100) / 100,
      };
    }).filter(d => d.events > 0);
  }, [daysInRange, events]);

  // 9. Events by Type (Pie Chart)
  const eventsByType = useMemo(() => {
    const typeMap = {};
    events.forEach(event => {
      if (!typeMap[event.name]) {
        typeMap[event.name] = { name: event.name, count: 0, hours: 0, color: event.color };
      }
      typeMap[event.name].count++;
      const [startH, startM] = event.start_time.split(':').map(Number);
      const [endH, endM] = event.end_time.split(':').map(Number);
      typeMap[event.name].hours += ((endH * 60 + endM) - (startH * 60 + startM)) / 60;
    });
    return Object.values(typeMap).sort((a, b) => b.hours - a.hours);
  }, [events]);

  return (
    <div className="space-y-6" data-testid="charts-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard/reports')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <TrendingUp className="w-6 h-6" />
              Charts & Analytics
            </h1>
            <p className="text-muted-foreground">
              Visual insights into your scheduling data
            </p>
          </div>
        </div>
        
        {/* Date Range Selector */}
        <div className="flex items-center gap-2">
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-[180px]" data-testid="chart-date-range">
              <SelectValue placeholder="Select period" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="this_week">This Week</SelectItem>
              <SelectItem value="last_week">Last Week</SelectItem>
              <SelectItem value="last_4_weeks">Last 4 Weeks</SelectItem>
              <SelectItem value="this_month">This Month</SelectItem>
              <SelectItem value="last_month">Last Month</SelectItem>
              <SelectItem value="custom">Custom Range</SelectItem>
            </SelectContent>
          </Select>
          
          {dateRange === "custom" && (
            <div className="flex items-center gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm">
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    {customStart ? format(customStart, "MMM d") : "Start"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={customStart}
                    onSelect={setCustomStart}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              <span className="text-muted-foreground">to</span>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm">
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    {customEnd ? format(customEnd, "MMM d") : "End"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={customEnd}
                    onSelect={setCustomEnd}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          )}
        </div>
      </div>

      {/* Date Range Display */}
      <div className="text-sm text-muted-foreground">
        Showing data from <span className="font-medium">{format(startDate, "MMMM d, yyyy")}</span> to <span className="font-medium">{format(endDate, "MMMM d, yyyy")}</span>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">Loading charts...</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Hours per Employee */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Users className="w-4 h-4" />
                Hours by Employee
              </CardTitle>
              <CardDescription>Total billable hours per team member</CardDescription>
            </CardHeader>
            <CardContent>
              {hoursPerEmployeeData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={hoursPerEmployeeData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis type="number" className="text-xs" />
                    <YAxis dataKey="name" type="category" width={60} className="text-xs" />
                    <Tooltip content={<CustomTooltip suffix="h" />} />
                    <Bar dataKey="hours" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                  No shift data for this period
                </div>
              )}
            </CardContent>
          </Card>

          {/* Cost per Employee */}
          {hasWageCosts && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <DollarSign className="w-4 h-4" />
                  Cost by Employee
                </CardTitle>
                <CardDescription>Total labor cost per team member</CardDescription>
              </CardHeader>
              <CardContent>
                {costPerEmployeeData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={costPerEmployeeData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis type="number" className="text-xs" />
                      <YAxis dataKey="name" type="category" width={60} className="text-xs" />
                      <Tooltip content={<CustomTooltip prefix={currencySymbol} />} />
                      <Bar dataKey="cost" fill="#06b6d4" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                    No cost data for this period
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Shifts by Day of Week */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <BarChart3 className="w-4 h-4" />
                Shifts by Day of Week
              </CardTitle>
              <CardDescription>Which days are busiest</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={shiftsPerDayOfWeek}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="name" className="text-xs" />
                  <YAxis className="text-xs" />
                  <Tooltip content={<CustomTooltip suffix=" shifts" />} />
                  <Bar dataKey="shifts" fill="#10b981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Daily Hours Trend */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Clock className="w-4 h-4" />
                Daily Hours Trend
              </CardTitle>
              <CardDescription>Total scheduled hours over time</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <RechartsLineChart data={dailyHoursTrend}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="date" className="text-xs" />
                  <YAxis className="text-xs" />
                  <Tooltip content={<CustomTooltip suffix="h" />} />
                  <Line 
                    type="monotone" 
                    dataKey="hours" 
                    stroke="#8b5cf6" 
                    strokeWidth={2}
                    dot={{ fill: "#8b5cf6", strokeWidth: 2 }}
                  />
                </RechartsLineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Daily Cost Trend */}
          {hasWageCosts && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <TrendingUp className="w-4 h-4" />
                  Daily Cost Trend
                </CardTitle>
                <CardDescription>Labor costs over time</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={dailyCostTrend}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="date" className="text-xs" />
                    <YAxis className="text-xs" />
                    <Tooltip content={<CustomTooltip prefix={currencySymbol} />} />
                    <Area 
                      type="monotone" 
                      dataKey="cost" 
                      stroke="#06b6d4" 
                      fill="#06b6d4" 
                      fillOpacity={0.3}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Cost Breakdown Pie */}
          {hasWageCosts && costBreakdownData.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <PieChart className="w-4 h-4" />
                  Cost Breakdown
                </CardTitle>
                <CardDescription>Where your labor budget goes</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <RechartsPieChart>
                    <Pie
                      data={costBreakdownData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {costBreakdownData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value) => `${currencySymbol}${value.toFixed(2)}`}
                    />
                  </RechartsPieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Staff Count per Day */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Users className="w-4 h-4" />
                Daily Staffing Levels
              </CardTitle>
              <CardDescription>Number of staff and shifts per day</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={staffCountPerDay}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="date" className="text-xs" />
                  <YAxis className="text-xs" />
                  <Tooltip />
                  <Area 
                    type="monotone" 
                    dataKey="staff" 
                    name="Unique Staff"
                    stroke="#8b5cf6" 
                    fill="#8b5cf6" 
                    fillOpacity={0.3}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="shifts" 
                    name="Total Shifts"
                    stroke="#10b981" 
                    fill="#10b981" 
                    fillOpacity={0.3}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Events per Day */}
          {hasEventAccess && eventsPerDay.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <PartyPopper className="w-4 h-4" />
                  Events Overview
                </CardTitle>
                <CardDescription>Event count and hours by day</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={eventsPerDay}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="date" className="text-xs" />
                    <YAxis yAxisId="left" className="text-xs" />
                    <YAxis yAxisId="right" orientation="right" className="text-xs" />
                    <Tooltip />
                    <Bar yAxisId="left" dataKey="events" name="Events" fill="#ec4899" radius={[4, 4, 0, 0]} />
                    <Bar yAxisId="right" dataKey="hours" name="Hours" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Events by Type */}
          {hasEventAccess && eventsByType.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <PieChart className="w-4 h-4" />
                  Events by Type
                </CardTitle>
                <CardDescription>Hours breakdown by event name</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <RechartsPieChart>
                    <Pie
                      data={eventsByType}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="hours"
                      label={({ name, hours }) => `${name} (${hours.toFixed(1)}h)`}
                    >
                      {eventsByType.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value, name, props) => [`${value.toFixed(1)}h`, props.payload.name]}
                    />
                  </RechartsPieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export default Charts;
